import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAdminEvents, getReport, adminLogout } from '../../services/adminApi';
import type { Event } from '../../types';
import type { ReportData, QuestionStat } from '../../services/adminApi';
import './Admin.css';

export default function Reports() {
  const { eventId } = useParams<{ eventId?: string }>();
  const navigate = useNavigate();

  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(
    eventId ? parseInt(eventId) : null
  );
  const [report, setReport] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadEvents();
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      loadReport(selectedEventId);
    } else {
      setReport(null);
    }
  }, [selectedEventId]);

  const loadEvents = async () => {
    try {
      const data = await getAdminEvents();
      setEvents(data);
      if (data.length > 0 && !selectedEventId) {
        setSelectedEventId(data[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load events');
    }
  };

  const loadReport = async (id: number) => {
    try {
      setLoading(true);
      setError('');
      const data = await getReport(id);
      setReport(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load report');
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await adminLogout();
    navigate('/admin/login');
  };

  return (
    <div className="admin-page">
      <div className="admin-layout">
        <div className="admin-header">
          <h1 className="admin-header__title">Survey Reports</h1>
          <div className="admin-header__actions">
            <button onClick={() => navigate('/admin')} className="btn btn--secondary">
              Dashboard
            </button>
            <button onClick={handleLogout} className="btn btn--ghost">
              Logout
            </button>
          </div>
        </div>

        <div className="admin-section">
          <label className="admin-form__label">Select Event</label>
          <select
            value={selectedEventId || ''}
            onChange={(e) => setSelectedEventId(e.target.value ? parseInt(e.target.value) : null)}
            className="admin-form__select"
            style={{ maxWidth: '400px' }}
          >
            <option value="">-- Select an event --</option>
            {events.map((event) => (
              <option key={event.id} value={event.id}>
                {event.name} ({new Date(event.event_date).toLocaleDateString()})
              </option>
            ))}
          </select>
        </div>

        {error && <div className="admin-form__error">{error}</div>}

        {loading && <div className="loading">Loading report...</div>}

        {report && (
          <>
            <div className="report-stats">
              <div className="report-stat">
                <div className="report-stat__label">Total Sessions</div>
                <div className="report-stat__value">{report.stats.total_sessions}</div>
              </div>
              <div className="report-stat">
                <div className="report-stat__label">Completed</div>
                <div className="report-stat__value">{report.stats.completed_sessions}</div>
              </div>
              <div className="report-stat">
                <div className="report-stat__label">In Progress</div>
                <div className="report-stat__value">{report.stats.in_progress_sessions}</div>
              </div>
              <div className="report-stat">
                <div className="report-stat__label">Participants</div>
                <div className="report-stat__value">{report.stats.participant_count}</div>
              </div>
            </div>

            <div className="admin-section">
              <h2 className="admin-section__title">Question Results</h2>
              {report.question_stats.map((stat: QuestionStat) => (
                <div key={stat.question_id} className="report-question">
                  <h3 className="report-question__title">{stat.question_text}</h3>
                  <div className="report-question__stats">
                    <div className="report-question__stat">
                      <span className="report-question__stat-label">Type:</span>
                      {stat.question_type}
                    </div>
                    <div className="report-question__stat">
                      <span className="report-question__stat-label">Responses:</span>
                      {stat.total_responses}
                    </div>
                    {stat.average !== undefined && (
                      <>
                        <div className="report-question__stat">
                          <span className="report-question__stat-label">Average:</span>
                          {stat.average.toFixed(2)}
                        </div>
                        <div className="report-question__stat">
                          <span className="report-question__stat-label">Range:</span>
                          {stat.min} - {stat.max}
                        </div>
                      </>
                    )}
                  </div>

                  {stat.question_type === 'STAR_RATING' && stat.distribution && (
                    <div className="report-distribution">
                      {Object.entries(stat.distribution)
                        .sort((a, b) => parseFloat(b[0]) - parseFloat(a[0]))
                        .map(([value, count]) => {
                          const percentage = (count / stat.total_responses) * 100;
                          return (
                            <div key={value} className="report-distribution__item">
                              <span style={{ minWidth: '3rem' }}>{value} ⭐</span>
                              <div className="report-distribution__bar">
                                <div
                                  className="report-distribution__fill"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                              <span style={{ minWidth: '3rem', textAlign: 'right' }}>
                                {count} ({percentage.toFixed(1)}%)
                              </span>
                            </div>
                          );
                        })}
                    </div>
                  )}

                  {stat.question_type === 'CHOOSE_MULTIPLE' && stat.option_counts && (
                    <div className="report-distribution">
                      {Object.entries(stat.option_counts)
                        .sort((a, b) => b[1] - a[1])
                        .map(([optionId, count]) => {
                          const label = stat.option_labels?.[parseInt(optionId)] || `Option ${optionId}`;
                          const percentage = (count / stat.total_responses) * 100;
                          return (
                            <div key={optionId} className="report-distribution__item">
                              <span style={{ minWidth: '10rem' }}>{label}</span>
                              <div className="report-distribution__bar">
                                <div
                                  className="report-distribution__fill"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                              <span style={{ minWidth: '3rem', textAlign: 'right' }}>
                                {count} ({percentage.toFixed(1)}%)
                              </span>
                            </div>
                          );
                        })}
                    </div>
                  )}

                  {stat.question_type === 'TEXT' && stat.text_responses && stat.text_responses.length > 0 && (
                    <div className="report-text-responses">
                      {stat.text_responses.map((text, idx) => (
                        <div key={idx} className="report-text-response">
                          {text}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
